/**
 * Project sessions (W1-16 app-integration; ARCHITECTURE.md §5, §9, §10; DESIGN.md §5.13): opening and closing the
 * chart this tab edits, and everything that lives exactly as long as it is open.
 *
 * Open (`open(id)`, serialised with every other open/close of the session):
 *  1. `persist.openProject` (Web Lock, newest snapshot + journal replay + history fold). A PersistError (e.g.
 *     'lockedElsewhere': the Library offers "Switch to it / Edit here instead") reaches the caller unchanged.
 *  2. The DocStore with the folded undo history ("history survives reload"); a history the store cannot take is
 *     dropped (the History panel then says "Earlier changes: see Versions") instead of failing the open. Every
 *     committed transaction is forwarded to the persist client (`appendTxn`, outbox until durable), the live
 *     document is registered for the compaction self-heal (`attachLiveDoc`, CR-W1-13-1), and the store's notices
 *     (undo steps dropped by the memory cap, a stale history, a refused undo) become toasts.
 *  3. The Engine (purpose 'preview'; lowering options are derived from `doc.pkg` by the engine itself), then the
 *     TextureProvider (injected factory: asset bytes from persist, blur in the io worker, GIF delays from asset meta).
 *  4. The editor store: the persisted view state (validated, references to deleted lines dropped) or the per-user
 *     defaults (snap division and X grid from the settings), an active line, the selection bridge and pruning
 *     (state/connect.ts). Then the services announce the swap (`subscribe`): this is where the problems service
 *     hands the new document to the analysis worker (init with player settings, skin metrics and the reference
 *     viewport; CreateProblemsService feeds the worker, contract app/registry/types.ts).
 *  5. Audio: playback stops, the offset follows the document (META offset, info.yml offset, preview audio delay,
 *     also after every edit of them), the chart's song is read from storage and decoded in the background (the chart
 *     is usable at once; `media()` resolves when the song and the custom hitsounds are loaded), then the playhead
 *     returns to where it was when the chart was last open (unless the user already moved it).
 *  6. "Recovered N edits" when the previous session of the chart did not end (a crash; W3-01 UX-015: told apart
 *     from an ordinary reload by the session marks, sessionMarks.ts, and counted from where that session started),
 *     and the "Download recovery data" offer when the replay stopped early.
 * Close (`close()`): playback stops, the view state is written at once, the services drop the document (dialogs of
 * the project are answered, panels unmount), then `persist.closeProject` (flush, compaction = open path, lock
 * release; `close()` resolves false when that failed); the song is unloaded when no other chart took its place.
 * `abandon()` is the same without writing anything (another tab took the lock over: every write would be refused).
 *
 * Without Web Audio (the services' silent engine) songs and custom hitsounds reject with a FeatureUnavailableError:
 * the services already said that audio is not available, so the session adds no toast of its own.
 */

import type { MessageRef, Unsubscribe } from '../core/util/types';
import type { AssetId, ChartDoc, LineId, ProjectId } from '../core/model/types';
import type { ChangeSet } from '../core/ops/types';
import type { CreateEngine, Engine } from '../core/engine/types';
import { createEngine as defaultCreateEngine } from '../core/engine/engine';
import type { AudioEngine } from '../audio/types';
import { isAudioEngineError } from '../audio/errors';
import type { OpenedProject, PersistClient, PersistError } from '../persist/types';
import type { TextureProvider } from '../render/types';
import type {
  CreateDocStoreOptions,
  DocStore,
  DocStoreNotice,
  EditorStore,
  EditorStoreApi,
  PersistedViewState,
} from '../state/types';
import { createDocStore } from '../state/docStore';
import { connectDocStore } from '../state/connect';
import { ViewState } from '../state/viewState';
import type { SettingsService } from './settings/types';
import type { UiService } from './registry/types';
import { downloadBlob, extensionFor, fileStamp } from './shell/download';
import { createViewStatePersist, type ViewSaveTimers, type ViewStatePersist } from './viewStatePersist';
import { NO_SESSION_MARKS, recoveredEdits, type SessionMarks } from './sessionMarks';
import { m } from './session.messages';
import { isFeatureUnavailable } from './unavailable';

export type SessionState = 'closed' | 'opening' | 'open' | 'closing';

export interface ProjectSessionDeps {
  readonly persist: PersistClient;
  readonly editor: EditorStoreApi;
  readonly audio: AudioEngine;
  readonly settings: Pick<SettingsService, 'value'>;
  readonly ui: Pick<UiService, 'toast' | 'choose'>;
  /** Translates a MessageRef (reasons nested in toasts). */
  readonly tr: (ref: MessageRef) => string;
  /** The open chart's media for the renderer (null: images unavailable). */
  readonly createTextures: (store: DocStore) => TextureProvider | null;
  readonly createDocStore?: (opts: CreateDocStoreOptions) => DocStore;
  readonly createEngine?: CreateEngine;
  /** Full invariant check after every commit (dev builds). */
  readonly strict?: boolean;
  /** Engine verify mode: recompile from scratch every N syncs (0 = off). */
  readonly verifyEvery?: number;
  readonly viewSave?: { readonly debounceMs?: number; readonly maxWaitMs?: number; readonly timers?: ViewSaveTimers };
  /** Saves the recovery data of a chart (default: browser download). */
  readonly downloadRecovery?: (id: ProjectId, blob: Blob) => void;
  /** Developer diagnostics (default console.warn). */
  readonly report?: (message: string, err?: unknown) => void;
  /** Whether each chart's last session ended cleanly (default: none known; the app keeps them in localStorage). */
  readonly marks?: SessionMarks;
}

export interface ProjectSession {
  readonly state: SessionState;
  readonly projectId: ProjectId | null;
  readonly store: DocStore | null;
  readonly engine: Engine | null;
  readonly textures: TextureProvider | null;
  /** Resolves when the open chart's song and custom hitsounds are loaded (or failed to load). */
  media(): Promise<void>;
  /** Opens a chart (closing the open one first). Rejects with the PersistError of a refused open. */
  open(id: ProjectId): Promise<void>;
  /**
   * Saves and closes the open chart (no-op when none is open). Resolves false when storage did not close it cleanly
   * (the user was told; its changes stay in the journal and come back on the next open).
   */
  close(): Promise<boolean>;
  /** Drops the open chart without saving (its lock was taken over); resolves with its id (null: none open). */
  abandon(): Promise<ProjectId | null>;
  /** Writes the view state of the open chart now (page hidden or unloaded). */
  flushView(): void;
  /** The page is going away (`pagehide`): the session ended cleanly when storage has every change. */
  pageHidden(): void;
  /** The page came back (`pageshow` from the back/forward cache): the session is open again. */
  pageShown(): void;
  /** Called after the open chart (store, engine, textures) changed or the session state changed. */
  subscribe(cb: () => void): Unsubscribe;
  /** Closes the open chart and stops; later opens are refused. */
  dispose(): Promise<void>;
}

interface Live {
  readonly id: ProjectId;
  readonly store: DocStore;
  readonly engine: Engine;
  readonly textures: TextureProvider | null;
  readonly view: ViewStatePersist;
  /** Persistence wiring, detached after the persist client closed the project. */
  readonly persistOffs: Unsubscribe[];
  /** Editor and audio wiring, detached when the chart is unpublished. */
  readonly uiOffs: Unsubscribe[];
  /** Restored playhead (chart seconds). */
  readonly playheadSec: number;
  /** Where the playhead was parked while the song loads (a different position later means the user moved it). */
  readonly parkedSec: number;
  /** The remembered playhead while it waits for the song (the persisted view keeps it meanwhile). */
  readonly restore: PlayheadRestore;
  /** The user started playback since the open (the restored playhead is then left alone). */
  touched: boolean;
  /** Custom hitsounds this open already reported as failed (one toast per file and open). */
  readonly hitsoundsReported: Set<AssetId>;
  /** The document seq when this session opened the chart (its session mark). */
  readonly openedSeq: number;
}

interface PlayheadRestore {
  pending: boolean;
  readonly beat: number;
}

/** The PersistError code of a rejection, if it is one. */
export function persistErrorCode(err: unknown): PersistError['code'] | null {
  if (typeof err !== 'object' || err === null) return null;
  const code = (err as { readonly code?: unknown }).code;
  return code === 'lockedElsewhere' || code === 'lockLost' || code === 'notFound' || code === 'quota' || code === 'io'
    ? code
    : null;
}

/** One-line text of any failure. */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message === '' ? err.name : err.message;
  if (typeof err === 'object' && err !== null) {
    const msg = (err as { readonly message?: unknown }).message;
    if (typeof msg === 'string' && msg !== '') return msg;
  }
  return typeof err === 'string' && err !== '' ? err : fallback;
}

/** A placeholder id of a file an imported package did not contain (`missing:` + hash of its path). */
export function isMissingPlaceholder(id: string): boolean {
  return id.startsWith('missing:');
}

/** Storage could not find the bytes of an asset (they vanished, or were never written). */
export function isMissingBytes(err: unknown): boolean {
  return /bytes missing|file missing|not found/i.test(errorMessage(err, ''));
}

/** The file name of a package path ("audio/song.ogg" -> "song.ogg"; empty -> "the song"-less fallback ''). */
export function fileNameOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i >= 0 ? path.slice(i + 1) : path;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

/**
 * The line that is active when a chart opens without a remembered one: the first line in export order that can
 * hold notes and has some, else the first line that can hold notes, else the first line (null for an empty chart).
 */
export function defaultActiveLine(doc: ChartDoc): LineId | null {
  let firstPlain: LineId | null = null;
  const withNotes = new Set<LineId>();
  for (const n of doc.notes.values()) withNotes.add(n.line);
  for (const id of doc.lineOrder) {
    const line = doc.lines.get(id);
    if (line === undefined || line.attachUI !== null) continue;
    if (withNotes.has(id)) return id;
    firstPlain ??= id;
  }
  return firstPlain ?? doc.lineOrder[0] ?? null;
}

/** Every custom hitsound file of a chart: hitsound assets and the assets notes play. */
export function hitsoundAssets(doc: ChartDoc): Set<AssetId> {
  const out = new Set<AssetId>();
  for (const a of doc.assets.values()) if (a.role === 'hitsound' && a.missing !== true) out.add(a.id);
  for (const n of doc.notes.values()) if (n.hitsound?.kind === 'asset') out.add(n.hitsound.asset);
  return out;
}

/** Hitsound files a change introduced (new hitsound assets, notes that now play an asset). */
export function hitsoundAssetsOf(cs: ChangeSet): Set<AssetId> {
  const out = new Set<AssetId>();
  for (const op of cs.ops) {
    if (op.o === 'ins') {
      if (op.tbl === 'assets' && op.rec.role === 'hitsound') out.add(op.rec.id);
      if (op.tbl === 'notes' && op.rec.hitsound?.kind === 'asset') out.add(op.rec.hitsound.asset);
    } else if (op.o === 'set' && op.tbl === 'notes') {
      const hs = (op.next as { readonly hitsound?: unknown }).hitsound;
      if (typeof hs === 'object' && hs !== null && (hs as { readonly kind?: unknown }).kind === 'asset') {
        out.add((hs as { readonly asset: AssetId }).asset);
      }
    }
  }
  return out;
}

/**
 * Custom hitsounds an audio engine decoded, or found undecodable (assets are content-addressed: the same id never
 * decodes later). A load that failed for another reason (storage, the audio context) is not remembered: the next
 * load tries again.
 */
const hitsoundStates = new WeakMap<AudioEngine, Map<AssetId, 'decoded' | 'undecodable'>>();

function hitsoundStateOf(audio: AudioEngine): Map<AssetId, 'decoded' | 'undecodable'> {
  let map = hitsoundStates.get(audio);
  if (map === undefined) {
    map = new Map();
    hitsoundStates.set(audio, map);
  }
  return map;
}

/** Makes the audio engine drop its song, so playback runs silent and unbounded again. */
export function releaseMusic(audio: AudioEngine): void {
  if (audio.music !== null) audio.unloadMusic();
}

/** The audio offset of a document (META offset, info.yml offset, preview-only audio delay). */
export function applyOffset(audio: AudioEngine, doc: ChartDoc): void {
  audio.setOffset(doc.timing.offsetMs, doc.pkg.infoOffsetSec, doc.editor.previewAudioDelayMs);
}

function defaultDownload(id: ProjectId, blob: Blob): void {
  downloadBlob(blob, `phiedit-recovery-${id}-${fileStamp()}.${extensionFor(blob.type)}`);
}

export function createProjectSession(deps: ProjectSessionDeps): ProjectSession {
  const report =
    deps.report ??
    ((message: string, err?: unknown) => {
      console.warn(`session: ${message}`, err ?? '');
    });
  const makeStore = deps.createDocStore ?? createDocStore;
  const makeEngine = deps.createEngine ?? defaultCreateEngine;
  const download = deps.downloadRecovery ?? defaultDownload;
  const marks = deps.marks ?? NO_SESSION_MARKS;
  const listeners = new Set<() => void>();
  /** Writes waiting for storage (from the persist client's health; the page-hide check reads it synchronously). */
  let pendingWrites = 0;
  const offHealth = (() => {
    try {
      return deps.persist.onHealth((h) => {
        pendingWrites = h.pendingTxns;
      });
    } catch {
      return () => undefined;
    }
  })();

  let live: Live | null = null;
  let state: SessionState = 'closed';
  let disposed = false;
  /** Serialises open/close. */
  let chain: Promise<unknown> = Promise.resolve();
  /** Serialises song and hitsound loading (newest chart wins). */
  let mediaChain: Promise<void> = Promise.resolve();

  const notify = (): void => {
    for (const cb of [...listeners]) {
      try {
        cb();
      } catch (err) {
        report('a session listener failed', err);
      }
    }
  };
  const setState = (s: SessionState): void => {
    if (state === s) return;
    state = s;
    notify();
  };
  const enqueue = <T>(work: () => Promise<T>): Promise<T> => {
    const next = chain.then(work, work);
    chain = next.catch(() => undefined);
    return next;
  };
  const toastReason = (key: string, err: unknown, kind: 'warning' | 'error' = 'warning'): void => {
    const reason = isAudioEngineError(err) ? deps.tr(err.reason) : errorMessage(err, deps.tr({ key: m.unknownReason }));
    deps.ui.toast({ key, params: { reason } }, { kind });
  };

  // ------------------------------ store ------------------------------

  const createStore = (id: ProjectId, opened: OpenedProject): { store: DocStore; historyLost: boolean } => {
    const base = { projectId: id, doc: opened.doc, seq: opened.seq, strict: deps.strict ?? false } as const;
    try {
      return {
        store: makeStore({
          ...base,
          history: opened.history,
          historyPosition: opened.historyPosition,
          historyTruncated: opened.historyTruncated,
        }),
        historyLost: false,
      };
    } catch (err) {
      if (opened.history.length === 0) throw err;
      report('the folded undo history was refused; opening without it', err);
      return { store: makeStore({ ...base, historyTruncated: true }), historyLost: true };
    }
  };

  const onNotice = (n: DocStoreNotice): void => {
    deps.ui.toast(n.message, { kind: 'warning' });
    if (n.kind === 'replayRefused') deps.ui.toast(n.reason, { kind: 'warning' });
  };

  // ------------------------------ editor ------------------------------

  const restoredView = (json: OpenedProject['view'], doc: ChartDoc): PersistedViewState | null => {
    if (json === null) return null;
    const parsed = ViewState.parse(json);
    return parsed === null ? null : ViewState.sanitize(parsed, doc);
  };

  const openEditor = (id: ProjectId, view: PersistedViewState | null, doc: ChartDoc): void => {
    deps.editor.getState().openProject(id, view);
    const st = deps.editor.getState();
    if (view === null) {
      const editing = deps.settings.value.editing;
      st.setSnap({ div: editing.defaultSnapDiv, xGrid: editing.defaultXGrid });
    }
    if (st.activeLine === null || !doc.lines.has(st.activeLine)) {
      const line = defaultActiveLine(doc);
      if (line !== null) deps.editor.getState().setActiveLine(line);
    }
  };

  const closeEditor = (): void => {
    const st = deps.editor.getState() as EditorStore & { readonly closeProject?: () => void };
    if (typeof st.closeProject === 'function') st.closeProject();
    else st.clearSelection();
  };

  // ------------------------------ media ------------------------------

  const isCurrent = (l: Live): boolean => live === l && !disposed;
  /** Read through a call: the flag changes while an open awaits storage. */
  const isDisposed = (): boolean => disposed;

  /**
   * Decodes the custom hitsounds not yet in the engine. A file that could not be read (or the context was not
   * ready) is tried again by the next load that needs it (an edit using it, the next open); an undecodable one is not.
   * Each failing file is reported once per open. Without Web Audio nothing counts as failed: the services already
   * said that audio is not available.
   */
  const loadHitsounds = async (l: Live, assets: ReadonlySet<AssetId>): Promise<void> => {
    const states = hitsoundStateOf(deps.audio);
    let failed = 0;
    const fail = (asset: AssetId): void => {
      if (l.hitsoundsReported.has(asset)) return;
      l.hitsoundsReported.add(asset);
      failed++;
    };
    for (const asset of assets) {
      if (!isCurrent(l)) return;
      const state = states.get(asset);
      if (state === 'decoded') continue;
      if (state === 'undecodable') {
        fail(asset);
        continue;
      }
      try {
        const bytes = await deps.persist.getAsset(asset);
        await deps.audio.loadCustomHitsound(asset, toArrayBuffer(bytes));
        states.set(asset, 'decoded');
        l.hitsoundsReported.delete(asset);
      } catch (err) {
        if (isFeatureUnavailable(err)) break;
        if (isAudioEngineError(err, 'decodeFailed')) states.set(asset, 'undecodable');
        fail(asset);
        report(`custom hitsound ${asset} could not be loaded`, err);
      }
    }
    if (failed > 0 && isCurrent(l))
      deps.ui.toast({ key: m.hitsoundsFailed, params: { count: failed } }, { kind: 'warning' });
  };

  /** After the song loaded: back to the remembered playhead, unless the user played or moved it meanwhile. */
  const restorePlayhead = (l: Live): void => {
    if (!isCurrent(l)) return;
    const tr = deps.audio.transport;
    const at = tr.chartTime();
    if (!l.touched && !tr.state.playing && Math.abs(at - l.parkedSec) <= 1e-3 && Math.abs(at - l.playheadSec) > 1e-6) {
      tr.seek(l.playheadSec);
    }
    if (l.restore.pending) {
      // From now on the view follows the transport (the restored position, or where the user moved it).
      l.restore.pending = false;
      l.view.schedule();
    }
  };

  const loadMedia = async (l: Live): Promise<void> => {
    if (!isCurrent(l)) return;
    const music = l.store.doc.meta.music;
    const ref = music === null ? undefined : l.store.doc.assets.get(music);
    if (music === null) {
      releaseMusic(deps.audio);
    } else if (ref?.missing === true || isMissingPlaceholder(music)) {
      // An import without its song (a placeholder): say which file, offer to add it (UX-016).
      releaseMusic(deps.audio);
      deps.ui.toast(
        { key: m.songNotInPackage, params: { file: fileNameOf(ref?.path ?? '') } },
        { kind: 'warning', action: { label: { key: m.addSong }, command: 'assets.replace', args: { asset: music } } },
      );
    } else if (deps.audio.music?.asset !== music) {
      try {
        const bytes = await deps.persist.getAsset(music);
        if (!isCurrent(l)) return;
        await deps.audio.loadMusic(music, toArrayBuffer(bytes));
      } catch (err) {
        if (!isCurrent(l)) return;
        const expected =
          isAudioEngineError(err, 'superseded') || isAudioEngineError(err, 'disposed') || isFeatureUnavailable(err);
        if (!expected) {
          report(`the song ${music} could not be loaded`, err);
          if (persistErrorCode(err) === 'notFound' || isMissingBytes(err)) {
            // The file is gone from storage: name it (never its hash) and offer to choose it again.
            deps.ui.toast(
              { key: m.songMissing, params: { file: fileNameOf(ref?.path ?? '') } },
              {
                kind: 'warning',
                action: { label: { key: m.addSong }, command: 'assets.replace', args: { asset: music } },
              },
            );
          } else {
            toastReason(m.songLoadFailed, err);
          }
        }
      }
    }
    restorePlayhead(l);
    await loadHitsounds(l, hitsoundAssets(l.store.doc));
  };

  const queueMedia = (work: () => Promise<void>): Promise<void> => {
    mediaChain = mediaChain.then(work).catch((err: unknown) => {
      report('loading media failed', err);
    });
    return mediaChain;
  };

  // ------------------------------ open ------------------------------

  /** The playhead as a beat of the open chart (persisted with the view). */
  const playheadBeat = (engine: Engine): number => {
    try {
      return Math.max(0, engine.tempo.beatF64At(deps.audio.transport.chartTime()));
    } catch {
      return 0;
    }
  };

  const assemble = (id: ProjectId, opened: OpenedProject): { live: Live; historyLost: boolean } => {
    const { store, historyLost } = createStore(id, opened);
    const persistOffs: Unsubscribe[] = [];
    const uiOffs: Unsubscribe[] = [];
    const undo = (): void => {
      for (const off of [...uiOffs.splice(0), ...persistOffs.splice(0)].reverse()) off();
    };
    try {
      // Before anything can commit: every committed transaction reaches the journal.
      persistOffs.push(
        store.subscribeCommits((txn) => {
          deps.persist.appendTxn(id, txn);
        }),
      );
      // The replay-error self-heal posts the live document as the new snapshot (PersistClient.attachLiveDoc).
      persistOffs.push(deps.persist.attachLiveDoc(id, () => ({ doc: store.doc, seq: store.seq })));
      persistOffs.push(store.subscribeNotices(onNotice));

      const engine = makeEngine(store, { purpose: 'preview', verifyEvery: deps.verifyEvery ?? 0 });
      persistOffs.push(() => {
        engine.dispose();
      });
      let textures: TextureProvider | null = null;
      try {
        textures = deps.createTextures(store);
      } catch (err) {
        report('the texture provider could not be created', err);
      }
      if (textures !== null) {
        const t = textures;
        persistOffs.push(() => {
          t.dispose();
        });
      }

      const view = restoredView(opened.view, store.doc);
      let playheadSec = 0;
      if (view !== null && view.playheadBeat > 0) {
        try {
          playheadSec = Math.max(0, engine.tempo.secAtBeatF64(view.playheadBeat));
        } catch (err) {
          report('the remembered playhead could not be converted', err);
        }
      }
      openEditor(id, view, store.doc);
      uiOffs.push(
        connectDocStore(store, deps.editor, {
          restoreActiveLine: () => deps.settings.value.editing.jumpToChangeOnUndo,
        }),
      );

      // Audio: stop the previous chart, follow this one's offset. The playhead goes to the remembered position at
      // once when the song is already loaded (same song as the chart before), else it waits at 0 for the song.
      const transport = deps.audio.transport;
      if (transport.state.playing) transport.stop({ returnToStart: false });
      applyOffset(deps.audio, store.doc);
      const music = store.doc.meta.music;
      const parkedSec = music !== null && deps.audio.music?.asset === music ? playheadSec : 0;
      transport.seek(parkedSec);
      // Until the remembered playhead is restored (or the user moves on), the view keeps it.
      const restore: PlayheadRestore = { pending: parkedSec !== playheadSec, beat: view?.playheadBeat ?? 0 };
      const l: Live = {
        id,
        store,
        engine,
        textures,
        view: createViewStatePersist({
          projectId: id,
          editor: deps.editor,
          persist: deps.persist,
          snapshot: () =>
            ViewState.snapshot(deps.editor.getState(), restore.pending ? restore.beat : playheadBeat(engine)),
          changes: [
            (cb) =>
              deps.audio.transport.onChange(() => {
                cb();
              }),
          ],
          ...deps.viewSave,
          onError: (err) => {
            report('the view state could not be saved', err);
          },
        }),
        persistOffs,
        uiOffs,
        playheadSec,
        parkedSec,
        restore,
        touched: false,
        hitsoundsReported: new Set(),
        openedSeq: store.seq,
      };
      uiOffs.push(
        store.subscribe((cs) => {
          if (cs.dirty.timing || cs.dirty.editor || cs.dirty.pkg) applyOffset(deps.audio, store.doc);
          if (cs.dirty.assets || cs.dirty.lineNotes.size > 0) {
            const added = hitsoundAssetsOf(cs);
            if (added.size > 0) void queueMedia(() => loadHitsounds(l, added));
          }
        }),
        transport.onChange((ts) => {
          if (!ts.playing) return;
          l.touched = true;
          l.restore.pending = false;
        }),
      );
      return { live: l, historyLost };
    } catch (err) {
      undo();
      if (deps.editor.getState().projectId === id) closeEditor();
      throw err;
    }
  };

  const offerRecovery = async (id: ProjectId, error: string): Promise<void> => {
    const choice = await deps.ui.choose<'download' | 'continue'>({
      title: { key: m.replayErrorTitle },
      body: { key: m.replayErrorBody, params: { error } },
      options: [
        { value: 'download', label: { key: m.replayErrorDownload } },
        { value: 'continue', label: { key: m.replayErrorContinue } },
      ],
    });
    if (choice !== 'download') return;
    try {
      download(id, await deps.persist.recoveryData(id));
    } catch (err) {
      deps.ui.toast(
        { key: m.recoveryFailed, params: { error: errorMessage(err, deps.tr({ key: m.unknownReason })) } },
        { kind: 'error' },
      );
    }
  };

  const doOpen = async (id: ProjectId): Promise<void> => {
    if (disposed) throw new Error('the project session was disposed');
    if (live?.id === id) return;
    if (live !== null) await doClose(live, 'close');
    setState('opening');
    let opened: OpenedProject;
    try {
      opened = await deps.persist.openProject(id);
    } catch (err) {
      setState('closed');
      throw err;
    }
    let built: { live: Live; historyLost: boolean };
    try {
      if (isDisposed()) throw new Error('the project session was disposed');
      built = assemble(id, opened);
    } catch (err) {
      setState('closed');
      await deps.persist.closeProject(id).catch((e: unknown) => {
        report('closing a project that failed to open', e);
      });
      throw err;
    }
    const l = built.live;
    live = l;
    state = 'open';
    notify();
    void queueMedia(() => loadMedia(l));
    const previous = marks.previous(id);
    marks.opened(id, l.openedSeq);
    const recovered = recoveredEdits(previous, opened.replayed, opened.seq);
    if (recovered !== null) {
      deps.ui.toast({ key: m.recovered, params: { count: recovered } }, { kind: 'info' });
    }
    if (built.historyLost) deps.ui.toast({ key: m.historyNotRestored }, { kind: 'warning' });
    if (opened.replayError !== null) void offerRecovery(id, opened.replayError);
  };

  // ------------------------------ close ------------------------------

  /** Resolves false when storage did not close the chart cleanly. */
  const doClose = async (l: Live, mode: 'close' | 'abandon'): Promise<boolean> => {
    if (live !== l) return true;
    setState('closing');
    const transport = deps.audio.transport;
    if (transport.state.playing) transport.stop({ returnToStart: false });
    if (mode === 'close') l.view.flush();
    l.view.dispose();
    for (const off of l.uiOffs.splice(0).reverse()) off();
    live = null;
    closeEditor();
    transport.seek(0);
    notify();
    let closed = true;
    try {
      await deps.persist.closeProject(l.id);
      if (mode === 'close') marks.ended(l.id);
    } catch (err) {
      closed = false;
      const code = persistErrorCode(err);
      if (mode === 'close' && code !== 'lockLost') {
        report(`project ${l.id} was not closed cleanly`, err);
        toastReason(m.closeFailed, err);
      }
    } finally {
      for (const off of l.persistOffs.splice(0).reverse()) {
        try {
          off();
        } catch (err) {
          report('releasing a closed chart failed', err);
        }
      }
    }
    void queueMedia(() => {
      if (live === null && !disposed) releaseMusic(deps.audio);
      return Promise.resolve();
    });
    setState('closed');
    return closed;
  };

  return {
    get state() {
      return state;
    },
    get projectId() {
      return live?.id ?? null;
    },
    get store() {
      return live?.store ?? null;
    },
    get engine() {
      return live?.engine ?? null;
    },
    get textures() {
      return live?.textures ?? null;
    },
    media: () => mediaChain,
    open: (id) => enqueue(() => doOpen(id)),
    close: () => enqueue(() => (live === null ? Promise.resolve(true) : doClose(live, 'close'))),
    abandon: () =>
      enqueue(async () => {
        const l = live;
        if (l === null) return null;
        await doClose(l, 'abandon');
        return l.id;
      }),
    flushView() {
      live?.view.flush();
    },
    pageHidden() {
      if (live !== null && pendingWrites === 0) marks.ended(live.id);
    },
    pageShown() {
      if (live !== null) marks.opened(live.id, live.openedSeq);
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    dispose: () =>
      enqueue(async () => {
        if (live !== null) await doClose(live, 'close');
        disposed = true;
        listeners.clear();
        offHealth();
      }),
  };
}
